'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

export type UserRole = 'admin' | 'moderator' | 'participant';

export interface UserProfile {
  id: string;
  email: string | null;
  username: string | null;
  fullName: string;
  avatarUrl: string | null;
  role: UserRole;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserWallet {
  id: string;
  userId: string;
  walletAddress: string;
  walletName: string;
  network: 'BSC' | 'ETH' | 'SOL' | 'MATIC';
  isPrimary: boolean;
  connectedAt: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  wallets: UserWallet[];
  loading: boolean;
  role: UserRole | null;
  isAdmin: boolean;
  isModerator: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  connectWallet: (walletAddress: string, walletName: string, network: string) => Promise<UserWallet | null>;
  getUserWallets: () => Promise<UserWallet[]>;
  hasRole: (requiredRole: UserRole) => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [wallets, setWallets] = useState<UserWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const router = useRouter();

  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) return null;
      return {
        id: data.id,
        email: data.email,
        username: data.username,
        fullName: data.full_name,
        avatarUrl: data.avatar_url,
        role: data.role as UserRole,
        isActive: data.is_active,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch {
      return null;
    }
  };

  const fetchWallets = async (userId: string): Promise<UserWallet[]> => {
    try {
      const { data, error } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('user_id', userId)
        .order('connected_at', { ascending: false });
      if (error) return [];
      return (data || []).map((w: any) => ({
        id: w.id,
        userId: w.user_id,
        walletAddress: w.wallet_address,
        walletName: w.wallet_name,
        network: w.network,
        isPrimary: w.is_primary,
        connectedAt: w.connected_at,
      }));
    } catch {
      return [];
    }
  };

  useEffect(() => {
    // Initial loading state handled by onAuthStateChange firing INITIAL_SESSION
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Fetch data once upon auth change or initial session
        const [p, w] = await Promise.all([
          fetchProfile(session.user.id),
          fetchWallets(session.user.id),
        ]);
        setProfile(p);
        setWallets(w);
      } else {
        setProfile(null);
        setWallets([]);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
          username: metadata?.username || '',
          avatar_url: metadata?.avatarUrl || metadata?.avatar_url || '',
          role: 'participant',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    try {
      // 1. Attempt standard Supabase sign out
      await supabase.auth.signOut();
    } catch (err) {
      console.error('Supabase signOut error:', err);
    } finally {
      // 2. Manual Cookie Purge (Safety measure for bloated cookies)
      if (typeof document !== 'undefined') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
          if (name.includes('auth-token') || name.startsWith('sb-')) {
            document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure; Partitioned`;
            document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
          }
        }
      }
      
      // 3. Clear local storage PFX
      try {
        Object.keys(localStorage)
          .filter(k => k.startsWith('sb_'))
          .forEach(k => localStorage.removeItem(k));
      } catch {}

      // 4. Force hard reload and redirect (The Nuclear Option)
      // This ensures no stale React state remains.
      window.location.href = '/sign-up-login-screen';
    }
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const p = await fetchProfile(user.id);
    if (p) setProfile(p);
    return p;
  };

  // Connect a wallet address to the current user
  const connectWallet = async (
    walletAddress: string,
    walletName: string,
    network: string
  ): Promise<UserWallet | null> => {
    if (!user) return null;
    try {
      // Check if this is the first wallet (make it primary)
      const { count } = await supabase
        .from('user_wallets')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      const isPrimary = (count ?? 0) === 0;

      const { data, error } = await supabase
        .from('user_wallets')
        .upsert(
          {
            user_id: user.id,
            wallet_address: walletAddress,
            wallet_name: walletName,
            network: network.toUpperCase(),
            is_primary: isPrimary,
          },
          { onConflict: 'user_id,wallet_address' }
        )
        .select()
        .single();

      if (error) return null;

      const wallet: UserWallet = {
        id: data.id,
        userId: data.user_id,
        walletAddress: data.wallet_address,
        walletName: data.wallet_name,
        network: data.network,
        isPrimary: data.is_primary,
        connectedAt: data.connected_at,
      };

      setWallets((prev) => {
        const exists = prev.find((w) => w.walletAddress === walletAddress);
        if (exists) return prev.map((w) => (w.walletAddress === walletAddress ? wallet : w));
        return [wallet, ...prev];
      });

      return wallet;
    } catch {
      return null;
    }
  };

  // Get user wallets
  const getUserWallets = async (): Promise<UserWallet[]> => {
    if (!user) return [];
    const w = await fetchWallets(user.id);
    setWallets(w);
    return w;
  };

  // Role helpers
  const role = profile?.role ?? null;
  const isAdmin = role === 'admin';
  const isModerator = role === 'moderator' || role === 'admin';

  const hasRole = (requiredRole: UserRole): boolean => {
    if (!role) return false;
    const hierarchy: Record<UserRole, number> = { admin: 3, moderator: 2, participant: 1 };
    return hierarchy[role] >= hierarchy[requiredRole];
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    wallets,
    loading,
    role,
    isAdmin,
    isModerator,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    connectWallet,
    getUserWallets,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
